/**
 * NotificationToastContainer Component
 * Renders all active toast notifications using React Portal
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationToast from './NotificationToast';

const MAX_VISIBLE_TOASTS = 5;

export default function NotificationToastContainer() {
  const { notifications, markAsRead } = useNotifications();
  const [activeToasts, setActiveToasts] = useState([]);

  // Track which notifications are shown as toasts
  useEffect(() => {
    // Get unread notifications (newest first)
    const unreadNotifs = notifications.filter(n => !n.read).slice(0, MAX_VISIBLE_TOASTS);

    setActiveToasts(unreadNotifs);
  }, [notifications]);

  const handleDismiss = notificationId => {
    // Mark as read (removes from active toasts)
    markAsRead(notificationId);
  };

  // Create portal mount point if it doesn't exist
  useEffect(() => {
    let portalRoot = document.getElementById('toast-portal');
    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = 'toast-portal';
      portalRoot.setAttribute('aria-live', 'polite');
      portalRoot.setAttribute('aria-atomic', 'false');
      document.body.appendChild(portalRoot);
    }

    return () => {
      // Cleanup: remove portal if no toasts
      if (portalRoot && activeToasts.length === 0) {
        const timeoutId = setTimeout(() => {
          const portal = document.getElementById('toast-portal');
          if (portal && portal.children.length === 0) {
            portal.remove();
          }
        }, 500);

        // Clear timeout if component unmounts before delay completes
        return () => clearTimeout(timeoutId);
      }
    };
  }, [activeToasts.length]);

  const portalRoot = document.getElementById('toast-portal');
  if (!portalRoot || activeToasts.length === 0) {
    return null;
  }

  return createPortal(
    <div
      className="fixed top-4 right-4 z-50 flex flex-col items-end pointer-events-auto"
      role="region"
      aria-label="Notifications"
    >
      {activeToasts.map(notification => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onDismiss={() => handleDismiss(notification.id)}
        />
      ))}
    </div>,
    portalRoot
  );
}
